/**
 * Club & Pitstop status utilities — SINGLE SOURCE OF TRUTH
 * 
 * ALL status calculations go through this file. No duplicate parsers allowed.
 * 
 * STATUS CODES:
 * - 'open': Åben nu (Grøn)
 * - 'opens_later': Åbner senere i dag (Gul)
 * - 'closed': Lukket i dag (Rød)
 * - 'no_clubhouse': Køre-klub uden fast lokale (Lilla)
 */

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/* ────────────────────────────────────────────────
 *  CORE TIME PARSER — handles every known format
 * ────────────────────────────────────────────────
 * Supported inputs:
 *   "08:00-17:00"          standard
 *   "08.00-17.00."         dots + trailing period
 *   "08.00– 17.00."        em-dash, spaces, dots
 *   "08.00— 17.00."        en-dash, spaces, dots
 *   "10-23"                short (no minutes)
 *   "19:00-?"              question-mark close → open-ended
 *   "19:00-??"             double question-mark
 *   "18:00-"               empty close → open-ended
 *   "Man-Fre: 08:00-18:00" free-text day range
 *   "Lukket" / "Closed"    → null
 */
const parseTimeComponent = (str) => {
  const t = (str || '').trim().replace(/\.+$/, '').replace(/\./g, ':');
  if (!t || t === '?' || t === '??' || t === '-') return null;
  const parts = t.split(':');
  const h = parseInt(parts[0], 10);
  const m = parts.length > 1 ? parseInt(parts[1], 10) : 0;
  if (isNaN(h)) return null;
  return { hour: h, min: isNaN(m) ? 0 : m };
};

const CLOSED_WORDS = ['lukket', 'closed', 'geschlossen', 'lukke', 'fermé', 'chiuso', 'cerrado', 'zamknięte'];

const DAY_ABBR_MAP = {
  man: 'monday', tir: 'tuesday', ons: 'wednesday', tor: 'thursday',
  fre: 'friday', lør: 'saturday', søn: 'sunday',
  mon: 'monday', tue: 'tuesday', wed: 'wednesday', thu: 'thursday',
  fri: 'friday', sat: 'saturday', sun: 'sunday',
};
const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/**
 * Extract the relevant time range from a free-text string.
 * E.g. "Man-Fre: 08:00-18:00\nLør-Søn: 10:00-22:00" → "08:00-18:00" if today is a weekday.
 */
const extractTimeFromFreeText = (str) => {
  if (!str || typeof str !== 'string') return str;
  const lines = str.replace(/\\n/g, '\n').split('\n');
  const now = new Date();
  const todayIdx = DAY_ORDER.indexOf(DAY_NAMES[now.getDay()]);
  
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const m = trimmed.match(/^([A-Za-zæøåÆØÅ]+)\s*[-–—]\s*([A-Za-zæøåÆØÅ]+)\s*:\s*(.+)$/);
    if (m) {
      const fromDay = DAY_ABBR_MAP[m[1].toLowerCase().slice(0, 3)];
      const toDay = DAY_ABBR_MAP[m[2].toLowerCase().slice(0, 3)];
      if (fromDay && toDay) {
        const fromIdx = DAY_ORDER.indexOf(fromDay);
        const toIdx = DAY_ORDER.indexOf(toDay);
        const inRange = fromIdx <= toIdx
          ? (todayIdx >= fromIdx && todayIdx <= toIdx)
          : (todayIdx >= fromIdx || todayIdx <= toIdx);
        if (inRange) return m[3].trim();
      }
    }
  }
  // Fallback: if no day-range pattern was found, return the original string
  // (It might be a simple time range like "08:00-17:00")
  return lines[0]?.trim() || str;
};

/**
 * Parse ANY opening hours string into { openHour, openMin, closeHour, closeMin }
 * For multi-interval strings ("08-12, 14-17") returns the FIRST interval.
 * Use parseAllIntervals() to get every interval.
 */
const parseOpeningHours = (rawStr) => {
  const all = parseAllIntervals(rawStr);
  return all.length > 0 ? all[0] : null;
};

/**
 * Parse a multi-interval opening hours string into an array of parsed intervals.
 * Supports comma-separated split shifts: "08:00-12:00, 14:00-17:00"
 */
const parseAllIntervals = (rawStr) => {
  if (!rawStr || typeof rawStr !== 'string') return [];

  // Extract time range from possible free-text format first
  let hoursStr = extractTimeFromFreeText(rawStr);
  if (!hoursStr) return [];

  const lower = hoursStr.trim().toLowerCase();
  if (CLOSED_WORDS.includes(lower)) return [];

  // Split by comma for multi-interval support
  const segments = hoursStr.split(',').map(s => s.trim()).filter(Boolean);
  const result = [];
  for (const seg of segments) {
    const parsed = parseSingleInterval(seg);
    if (parsed) result.push(parsed);
  }
  return result;
};

const parseSingleInterval = (hoursStr) => {
  // Normalize: strip trailing dots, convert dots to colons, normalize all dash types
  let norm = hoursStr.trim().replace(/\.+$/, '').replace(/\./g, ':').replace(/[–—]/g, '-');

  // Split on dash — use only the first dash that separates open from close time
  const dashIdx = findTimeSeparator(norm);
  if (dashIdx === -1) return null;

  const openStr = norm.substring(0, dashIdx);
  const closeStr = norm.substring(dashIdx + 1);

  const open = parseTimeComponent(openStr);
  if (!open) return null;

  const close = parseTimeComponent(closeStr);

  return {
    openHour: open.hour,
    openMin: open.min,
    closeHour: close ? close.hour : 23,
    closeMin: close ? close.min : 59,
    openEnded: !close,
  };
};

/**
 * Find the index of the dash that separates open time from close time.
 * Handles "19:00-02:00" and also "19:00-22-00" (typo) gracefully.
 */
const findTimeSeparator = (str) => {
  // Find all dashes
  const dashes = [];
  for (let i = 0; i < str.length; i++) {
    if (str[i] === '-') dashes.push(i);
  }
  if (dashes.length === 0) return -1;
  if (dashes.length === 1) return dashes[0];
  
  // Multiple dashes — the first one after digits/colons is likely the separator
  // e.g. "19:00-02:00" → dash at index 5
  // e.g. "19:00-22-00" → dashes at 5 and 8, first one is the separator
  for (const idx of dashes) {
    const before = str.substring(0, idx).trim();
    // If the part before looks like a time (digits and colons), this is our separator
    if (/^\d{1,2}(:\d{2})?$/.test(before)) return idx;
  }
  
  // Fallback: use the first dash
  return dashes[0];
};

/**
 * Compute status from parsed hours and current time.
 * Returns 'open' | 'opens_later' | 'closed'
 */
const computeStatus = (parsed) => {
  if (!parsed) return 'closed';
  
  const now = new Date();
  const curMin = now.getHours() * 60 + now.getMinutes();
  const openMin = parsed.openHour * 60 + parsed.openMin;
  const closeMin = parsed.closeHour * 60 + parsed.closeMin;
  
  // Overnight (e.g. 20:00-02:00)
  if (closeMin <= openMin) {
    if (curMin >= openMin || curMin < closeMin) return 'open';
    if (curMin < openMin) return 'opens_later';
    return 'closed';
  }
  
  // Normal hours
  if (curMin >= openMin && curMin < closeMin) return 'open';
  if (curMin < openMin) return 'opens_later';
  return 'closed';
};

/**
 * Compute status across ALL intervals of a (possibly multi-interval) day string.
 * 'open' if ANY interval is open right now;
 * 'opens_later' if ANY future interval today;
 * else 'closed'.
 */
const computeStatusForDayString = (rawStr) => {
  const intervals = parseAllIntervals(rawStr);
  if (intervals.length === 0) return 'closed';
  let anyOpensLater = false;
  for (const iv of intervals) {
    const s = computeStatus(iv);
    if (s === 'open') return 'open';
    if (s === 'opens_later') anyOpensLater = true;
  }
  return anyOpensLater ? 'opens_later' : 'closed';
};

/* ──────────────────────────────────
 *  Alternative Opening Hours logic
 * ────────────────────────────────── */

const isNthWeekdayOfMonth = (date, n) => {
  return Math.ceil(date.getDate() / 7) === n;
};

const isLastWeekdayOfMonth = (date) => {
  const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  return date.getDate() + 7 > daysInMonth;
};

const matchesMonthlyPattern = (entry, now) => {
  const weekMap = { first: 1, second: 2, third: 3, fourth: 4 };
  const mw = entry.monthly_week || 'first';
  if (mw === 'last') return isLastWeekdayOfMonth(now);
  return isNthWeekdayOfMonth(now, weekMap[mw] || 1);
};

const checkAlternativeHours = (altHours) => {
  if (!Array.isArray(altHours) || altHours.length === 0) return null;

  const now = new Date();
  const todayKey = DAY_NAMES[now.getDay()];
  const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  for (const entry of altHours) {
    if (!entry.weekday || entry.weekday !== todayKey || !entry.time_from) continue;

    const pattern = entry.pattern || 'weekly';

    if (pattern === 'monthly') {
      if (!matchesMonthlyPattern(entry, now)) continue;
    } else {
      const freq = entry.frequency || 1;
      if (freq > 1 && entry.start_date) {
        try {
          const start = new Date(entry.start_date);
          start.setHours(0, 0, 0, 0);
          const weeksDiff = Math.floor((todayDate - start) / (7 * 86400000));
          if (weeksDiff < 0 || (weeksDiff % freq) !== 0) continue;
        } catch { /* skip */ }
      }
    }

    const timeStr = `${entry.time_from}-${entry.time_to || '?'}`;
    const parsed = parseOpeningHours(timeStr);
    const status = computeStatus(parsed);
    
    if (status === 'open') return { isOpen: true, opensLater: false, entry };
    if (status === 'opens_later') return { isOpen: false, opensLater: true, entry };
  }
  return null;
};

/* ──────────────────────────────
 *  PUBLIC API — exported functions
 * ────────────────────────────── */

export const getTodayOpeningHours = (openingHours) => {
  if (!openingHours) return null;
  const today = DAY_NAMES[new Date().getDay()];
  const hoursStr = openingHours[today];
  return parseOpeningHours(hoursStr);
};

export const getTodayHoursString = (openingHours) => {
  if (!openingHours) return null;
  const today = DAY_NAMES[new Date().getDay()];
  return openingHours[today] || null;
};

export const getOpeningTimeToday = (openingHours) => {
  const parsed = getTodayOpeningHours(openingHours);
  if (!parsed) return null;
  return `${String(parsed.openHour).padStart(2, '0')}:${String(parsed.openMin).padStart(2, '0')}`;
};

/**
 * CLUB status — always calculates locally for real-time accuracy on maps
 * Server status may be cached/stale, so we recalculate using opening_hours data
 */
export const getClubStatus = (club) => {
  // Extraordinary status overrides everything
  if (club.extraordinary_status === 'extra_open') return 'open';
  if (club.extraordinary_status === 'on_tour') return 'closed';

  if (club.no_clubhouse === true || club.club_type === 'riding_only') return 'no_clubhouse';

  const openingHours = club.opening_hours;
  const PHYSICAL = ['clubhouse', 'bar', 'pool', 'beds', 'tent', 'caravan', 'shower', 'food', 'grill', 'wifi', 'parking', 'kitchen'];
  const hasClubhouse = Array.isArray(club.facilities) && club.facilities.some(f => PHYSICAL.includes(f));
  const hasAnyHours = openingHours && typeof openingHours === 'object' &&
    Object.values(openingHours).some(v => v && typeof v === 'string' && v.trim().length > 0);
  const altHours = club.alternative_opening_hours;
  const hasAltHours = Array.isArray(altHours) && altHours.length > 0;

  if (!hasClubhouse && !hasAnyHours && !hasAltHours) return 'no_clubhouse';

  // Check standard hours
  if (hasAnyHours) {
    const today = DAY_NAMES[new Date().getDay()];
    const status = computeStatusForDayString(openingHours[today]);
    if (status === 'open') return 'open';
  }

  // Check alternative hours
  if (hasAltHours) {
    const altResult = checkAlternativeHours(altHours);
    if (altResult) {
      if (altResult.isOpen) return 'open';
      if (altResult.opensLater) return 'opens_later';
    }
  }

  // Opens later?
  if (hasAnyHours) {
    const today = DAY_NAMES[new Date().getDay()];
    if (computeStatusForDayString(openingHours[today]) === 'opens_later') return 'opens_later';
  }

  return 'closed';
};

/**
 * PITSTOP status — uses computed_status from server, falls back to local calculation
 * This is the ONLY pitstop status function. No duplicates.
 */
export const getPitstopStatus = (pitstop) => {
  // Server-computed status is the primary source
  // IMPORTANT: Pitstops can NEVER be "no_clubhouse" — that's only for riding clubs
  if (pitstop.computed_status && pitstop.computed_status !== 'unknown') {
    return pitstop.computed_status === 'no_clubhouse' ? 'closed' : pitstop.computed_status;
  }

  // Fallback: calculate locally
  const oh = pitstop.opening_hours;
  if (!oh) return 'closed';

  let hoursStr;
  if (typeof oh === 'object') {
    const today = DAY_NAMES[new Date().getDay()];
    hoursStr = oh[today];
  } else if (typeof oh === 'string') {
    hoursStr = oh;
  }

  const status = computeStatusForDayString(hoursStr);
  return status === 'no_clubhouse' ? 'closed' : status;
};

export const getStatusColor = (status) => {
  switch (status) {
    case 'open':
      return {
        border: '#22c55e', dot: '#22c55e', label: 'clubStatus.open',
        bgClass: 'bg-green-500', textClass: 'text-green-400',
        borderClass: 'border-green-500/30', bgLightClass: 'bg-green-500/10'
      };
    case 'opens_later':
      return {
        border: '#eab308', dot: '#eab308', label: 'clubStatus.opensLater',
        bgClass: 'bg-yellow-500', textClass: 'text-yellow-400',
        borderClass: 'border-yellow-500/30', bgLightClass: 'bg-yellow-500/10'
      };
    case 'no_clubhouse':
      return {
        border: '#a855f7', dot: '#a855f7', label: 'clubStatus.ridingClub',
        bgClass: 'bg-purple-500', textClass: 'text-purple-400',
        borderClass: 'border-purple-500/30', bgLightClass: 'bg-purple-500/10'
      };
    case 'closed':
    default:
      return {
        border: '#ef4444', dot: '#ef4444', label: 'clubStatus.closed',
        bgClass: 'bg-red-500', textClass: 'text-red-400',
        borderClass: 'border-red-500/30', bgLightClass: 'bg-red-500/10'
      };
  }
};

export const STATUS_CONFIG = {
  open: { title: 'Åbne klubber', color: 'bg-green-500', textColor: 'text-green-400', borderColor: 'border-green-500/30', bgColor: 'bg-green-500/10', icon: '🟢' },
  opens_later: { title: 'Åbner senere i dag', color: 'bg-yellow-500', textColor: 'text-yellow-400', borderColor: 'border-yellow-500/30', bgColor: 'bg-yellow-500/10', icon: '🟡' },
  closed: { title: 'Lukkede klubber', color: 'bg-red-500', textColor: 'text-red-400', borderColor: 'border-red-500/30', bgColor: 'bg-red-500/10', icon: '🔴' },
};
